import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  MetricsOverview,
  TimeSeriesChart,
  TopAppsTable,
  ActivityFeed,
  SystemHealth,
  DashboardFilters,
} from '../components/dashboard';
import { analyticsService } from '../services/analytics';
import { DashboardFilter, AnalyticsMetrics } from '../types';
import { useNotifications } from '../components/providers/NotificationProvider';
import { useAnalyticsWebSocket } from '../hooks';

export function Analytics() {
  const [filters, setFilters] = useState<DashboardFilter>({
    timeRange: '24h',
    refreshInterval: 30,
  });

  const [realTimeData, setRealTimeData] = useState<Partial<AnalyticsMetrics>>({});
  const { addNotification } = useNotifications();
  
  // WebSocket for real-time updates
  const { 
    isConnected: wsConnected, 
    lastMessage,
    subscribe: wsSubscribe 
  } = useAnalyticsWebSocket();

  // Main analytics data query
  const {
    data: metrics,
    isLoading: metricsLoading,
    error: metricsError,
    refetch: refetchMetrics,
  } = useQuery({
    queryKey: ['analytics', 'metrics', filters],
    queryFn: () => analyticsService.getMetrics(filters),
    staleTime: 30000, // 30 seconds
    refetchInterval: filters.refreshInterval * 1000,
  });

  // System health query
  const {
    data: systemHealth,
    isLoading: healthLoading,
    error: healthError,
  } = useQuery({
    queryKey: ['analytics', 'health'],
    queryFn: () => analyticsService.getSystemHealth(),
    staleTime: 15000, // 15 seconds
    refetchInterval: 15000,
  });

  // Handle WebSocket messages
  useEffect(() => {
    if (lastMessage?.type === 'metrics') {
      setRealTimeData(lastMessage.data);
    }
  }, [lastMessage]);

  // Subscribe to specific metric updates
  useEffect(() => {
    const unsubscribe = wsSubscribe('metrics', (message) => {
      if (message.data?.overview) {
        setRealTimeData(prev => ({
          ...prev,
          overview: { ...prev.overview, ...message.data.overview }
        }));
      }
    });

    return unsubscribe;
  }, [wsSubscribe]);

  // Handle errors
  useEffect(() => {
    if (metricsError) {
      addNotification({
        type: 'error',
        title: 'Analytics Error',
        message: 'Failed to load analytics data',
      });
    }
  }, [metricsError, addNotification]);

  useEffect(() => {
    if (healthError) {
      addNotification({
        type: 'warning',
        title: 'Health Check Failed',
        message: 'Unable to fetch system health status',
      });
    }
  }, [healthError, addNotification]);

  const handleRefresh = () => {
    refetchMetrics();
    addNotification({
      type: 'info',
      title: 'Refreshing',
      message: 'Analytics data refreshed',
    });
  };

  // Merge real-time data with main metrics
  const currentMetrics = metrics ? {
    ...metrics,
    overview: { ...metrics.overview, ...realTimeData.overview },
  } : undefined;

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
          Analytics Dashboard
        </h1>
        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1">
          Real-time monitoring and performance insights for your MCP connections
        </p>
      </div>

      {/* Filters */}
      <DashboardFilters
        filters={filters}
        onFiltersChange={setFilters}
        onRefresh={handleRefresh}
        isRefreshing={metricsLoading}
      />

      {/* Metrics Overview */}
      <MetricsOverview
        metrics={currentMetrics?.overview}
        isLoading={metricsLoading}
      />

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <TimeSeriesChart
          data={currentMetrics?.timeSeries.requests || []}
          title="Requests per Time"
          color="#3B82F6"
          type="area"
          yAxisLabel="Requests"
          loading={metricsLoading}
        />
        <TimeSeriesChart
          data={currentMetrics?.timeSeries.responseTime || []}
          title="Response Time"
          color="#10B981"
          type="line"
          yAxisLabel="Response Time (ms)"
          formatValue={(value) => `${value}ms`}
          loading={metricsLoading}
        />
      </div>

      {/* Secondary Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <TimeSeriesChart
          data={currentMetrics?.timeSeries.errors || []}
          title="Error Rate"
          color="#EF4444"
          type="area"
          yAxisLabel="Errors"
          loading={metricsLoading}
        />
        <TimeSeriesChart
          data={currentMetrics?.timeSeries.connections || []}
          title="Active Connections"
          color="#8B5CF6"
          type="line"
          yAxisLabel="Connections"
          loading={metricsLoading}
        />
      </div>

      {/* Data Tables and Info */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <TopAppsTable
          apps={currentMetrics?.topApps || []}
          loading={metricsLoading}
        />
        <div className="space-y-4 sm:space-y-6">
          <SystemHealth
            health={systemHealth || { overall: 'healthy', services: [] }}
            loading={healthLoading}
          />
        </div>
      </div>

      {/* Activity Feed */}
      <ActivityFeed
        activities={currentMetrics?.recentActivity || []}
        loading={metricsLoading}
      />

      {/* Status Bar */}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 sm:p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-xs sm:text-sm text-gray-600 dark:text-gray-400 space-y-2 sm:space-y-0">
          <div className="flex flex-wrap items-center gap-2 sm:gap-4">
            <span>
              Last updated: {new Date().toLocaleTimeString()}
            </span>
            <span>
              Auto-refresh: {filters.refreshInterval}s
            </span>
            {currentMetrics && (
              <span>
                {currentMetrics.overview.totalConnections} total connections
              </span>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <div className={`w-2 h-2 rounded-full ${
              wsConnected ? 'bg-green-500 animate-pulse' : 'bg-gray-400'
            }`}></div>
            <span>{wsConnected ? 'Live data (WebSocket)' : 'Polling mode'}</span>
          </div>
        </div>
      </div>
    </div>
  );
}